import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function EditProfilePage() {
    const { user, updateProfile } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setAvatar(user.avatar || '');
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await updateProfile({
                name: name.trim(),
                avatar: avatar.trim()
            });
            navigate('/profile');
        } catch (err) {
            console.error(err);
            setError('Failed to update profile.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!user) return null;

    return (
        <Container className="p-4">
            <Row className="justify-content-center">
                <Col md={8} lg={6}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-body-secondary">
                            <h1 className="h4 mb-0">Edit Profile</h1>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <div className="mb-4 text-center">
                                    <div className="mb-3 d-inline-block position-relative">
                                        {avatar ? (
                                            <Image
                                                src={avatar}
                                                roundedCircle
                                                style={{ width: 100, height: 100, objectFit: 'cover' }}
                                                onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/100?text=?')}
                                            />
                                        ) : (
                                            <div
                                                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white"
                                                style={{ width: 100, height: 100, fontSize: '2.5rem' }}
                                            >
                                                {user.name?.charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <Form.Text className="d-block text-muted">
                                        Preview of your avatar
                                    </Form.Text>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label>Display Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Avatar URL</Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={avatar}
                                        onChange={(e) => setAvatar(e.target.value)}
                                        placeholder="https://example.com/avatar.jpg"
                                    />
                                    <Form.Text className="text-muted">
                                        Enter a link to an image.
                                    </Form.Text>
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="outline-secondary" onClick={() => navigate('/profile')} disabled={isLoading}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={isLoading}>
                                        {isLoading ? <LoadingSpinner /> : 'Save Changes'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
